import {Alert, Button, Checkbox, Group, Loader, Modal, Stack, Text} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {useForm} from '@mantine/form'
import {notifications} from '@mantine/notifications'
import {IconStarFilled} from '@tabler/icons-react'
import {useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {toApiDateString, toLocalDate} from 'src/jobs/job-schedule-format'
import {useJob} from 'src/jobs/use-jobs'
import {ActiveYardSelector} from 'src/project-deployed-equipment/active-yard-selector'

import {stayOnRentWarning} from './partial-pickup-schema'
import {type PickupSelection, selectAll, selectedUnitIds} from './pickup-selection'
import {PickupUnitsChecklist} from './pickup-units-checklist'
import {useConfirmPickupReturns, usePickupUnits} from './use-work'

type ConfirmPickupFormValues = {
    // null selects every loaded unit.
    selection: PickupSelection | null
    yardId: string | null
    returnedOn: Date
    noDriverConfirmed: boolean
}

type ConfirmPickupButtonProps = {
    projectId: string
    jobId: string
    onConfirmed: () => void
    label?: string
}

function pickupResultMessage(returnedCount: number, stillOutCount: number): string {
    const back = `${returnedCount} ${returnedCount === 1 ? 'item' : 'items'} back.`
    return stillOutCount > 0 ? `${back} ${stillOutCount} still on rent.` : back
}

export function ConfirmPickupButton({
    projectId,
    jobId,
    onConfirmed,
    label = 'Complete pickup',
}: ConfirmPickupButtonProps) {
    const [opened, setOpened] = useState(false)
    const {data: units = [], isLoading} = usePickupUnits(projectId, jobId, {enabled: opened})
    const confirm = useConfirmPickupReturns(projectId, jobId)
    const form = useForm<ConfirmPickupFormValues>({
        initialValues: {
            selection: null,
            yardId: null,
            returnedOn: businessCalendarDate(),
            noDriverConfirmed: false,
        },
    })
    const {selection, yardId, returnedOn, noDriverConfirmed} = form.values
    const [error, setError] = useState<string | null>(null)
    const {data: job} = useJob(jobId, {enabled: opened})
    const noDriver = job != null && job.workerAssignments.length === 0
    const current = selection ?? selectAll(units)
    const stayingCount = units.length - selectedUnitIds(units, current).length

    async function handleConfirm() {
        if (!yardId) {
            setError('Return yard is required')
            return
        }
        setError(null)
        try {
            const result = await confirm.mutateAsync({
                yardId,
                returnedOn: toApiDateString(returnedOn),
                rentalAssetIds: selectedUnitIds(units, current),
            })
            setOpened(false)
            notifications.show({
                color: 'green',
                title: 'Pickup completed',
                message: pickupResultMessage(result.returnedCount, result.stillOutCount),
            })
            onConfirmed()
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Could not complete the pickup')
        }
    }

    return (
        <>
            <Button
                variant="light"
                onClick={() => {
                    form.setValues({selection: null, noDriverConfirmed: false})
                    setError(null)
                    setOpened(true)
                }}
            >
                {label}
            </Button>
            <Modal opened={opened} onClose={() => setOpened(false)} title="What came back?">
                <Stack gap="md">
                    {error ? <Alert color="red">{error}</Alert> : null}
                    {isLoading ? (
                        <Loader size="sm" aria-label="Loading units" />
                    ) : units.length === 0 ? (
                        <Text size="sm">All units already returned.</Text>
                    ) : (
                        <>
                            <Text size="sm" c="dimmed">
                                Untick units still on site.
                            </Text>
                            <PickupUnitsChecklist
                                units={units}
                                selection={current}
                                onChange={(value) => form.setFieldValue('selection', value)}
                            />
                        </>
                    )}
                    {stayingCount > 0 ? (
                        <Alert
                            color="orange"
                            variant="light"
                            icon={<IconStarFilled size={16} />}
                            data-testid="partial-pickup-warning"
                        >
                            {stayOnRentWarning(stayingCount)}
                        </Alert>
                    ) : null}
                    {noDriver ? (
                        <Alert color="yellow" variant="light" data-testid="pickup-no-driver">
                            <Stack gap="xs">
                                <Text size="sm">No driver is assigned to this pickup.</Text>
                                <Checkbox
                                    label="Complete without a driver"
                                    {...form.getInputProps('noDriverConfirmed', {
                                        type: 'checkbox',
                                    })}
                                />
                            </Stack>
                        </Alert>
                    ) : null}
                    <ActiveYardSelector
                        value={yardId}
                        onChange={(value) => form.setFieldValue('yardId', value)}
                    />
                    <DateInput
                        label="Came back on"
                        value={returnedOn}
                        maxDate={businessCalendarDate()}
                        onChange={(value) =>
                            value && form.setFieldValue('returnedOn', toLocalDate(value))
                        }
                    />
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setOpened(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => void handleConfirm()}
                            loading={confirm.isPending}
                            disabled={noDriver && !noDriverConfirmed}
                        >
                            Confirm and complete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    )
}
